/**
 * Toolbar Buttons
 * Reusable button components for the RichTextEditor toolbar
 */
import React, { useCallback } from 'react';
import { HistoryEditor } from 'slate-history';
import { useSlate } from 'slate-react';
import { TEXT_ALIGN_TYPES } from '../constants';
import EditorButton from '../EditorButton/EditorButton';
import { isBlockActive, isMarkActive, toggleBlock, toggleMark } from '../editor.utils';
import '../RichTextEditor.scss';

// ============================================================================
// Block Button (headings, lists, quotes, alignment)
// ============================================================================

export const BlockButton = ({ format, icon }) => {
  const editor = useSlate();
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
  );

  const onMouseDown = useCallback(
    (event) => {
      event.preventDefault();
      toggleBlock(editor, format);
    },
    [editor, format]
  );

  return (
    <EditorButton
      active={isActive}
      onMouseDown={onMouseDown}
    >
      <span className='editor-icon material-icons'>{icon}</span>
    </EditorButton>
  );
};

BlockButton.displayName = 'BlockButton';

// ============================================================================
// Mark Button (bold, italic, underline, strikethrough)
// ============================================================================

export const MarkButton = ({ format, icon }) => {
  const editor = useSlate();
  const isActive = isMarkActive(editor, format);

  const onMouseDown = useCallback(
    (event) => {
      event.preventDefault();
      toggleMark(editor, format);
    },
    [editor, format]
  );

  return (
    <EditorButton
      active={isActive}
      onMouseDown={onMouseDown}
    >
      <span className='editor-icon material-icons'>{icon}</span>
    </EditorButton>
  );
};

MarkButton.displayName = 'MarkButton';

// ============================================================================
// History Button (undo/redo)
// ============================================================================

export const HistoryButton = ({ type, icon }) => {
  const editor = useSlate();
  const canPerform =
    type === 'undo' ? editor.history.undos.length > 0 : editor.history.redos.length > 0;

  const onMouseDown = useCallback(
    (event) => {
      event.preventDefault();
      if (type === 'undo') {
        HistoryEditor.undo(editor);
      } else {
        HistoryEditor.redo(editor);
      }
    },
    [editor, type]
  );

  return (
    <EditorButton
      variant='history'
      active={canPerform}
      disabled={!canPerform}
      onMouseDown={onMouseDown}
    >
      <span className='editor-icon material-icons'>{icon}</span>
    </EditorButton>
  );
};

HistoryButton.displayName = 'HistoryButton';
